import { useCallback, useRef } from "react";
import {
  getAvatarUrl,
  getDefaultAvatarForUser,
  parseSettings,
  removeUnknownInputIdsFromFilters,
} from "../utils/utils";
import http from "../../common/utilities/HttpModule";
import { User, UserSettingsSorters } from "../interfaces/interfaces";
import { changeIsLoading, login, updateUserSettings } from "../store/userSlice";
import {
  ARCHIVE_SORTER_KEY,
  OVERVIEW_SORTER_KEY,
} from "../../common/dataRefining";
import {
  setActiveSorter,
  setFilterArrayArchive,
  setFilterArrayOverview,
  setShouldInitFilteringData,
} from "../../common/dataRefining/store/dataRefiningSlice";
import { DocumentSetColumns } from "../../documentSet/interfaces/documentSet";
import Sentry from "../../sentry";
import { useAppDispatch, useAppSelector } from "../../app";
import { useTranslation } from "react-i18next";
import useAppConfig from "../../app/hooks/useAppConfig";
import { setHasColumnItemsFetchingError } from "../../documentSet/store/documentSetSlice";
import { fetchUserData } from "../utils/http";
import useAuthenticationTokens from "../../authProvider/hooks/useAuthenticationTokens";
import { ACCESS_TOKEN_COOKIE } from "../../authProvider/constants";

const useAuthentication = () => {
  const dispatch = useAppDispatch();
  const isExecutingRef = useRef(false);
  const hasExecutedRef = useRef(false);

  const { setToken } = useAuthenticationTokens();

  const { overviewMap } = useAppSelector((state) => state.appReducer);

  const {
    i18n: { changeLanguage },
  } = useTranslation("personalPage");

  const fetchAndSetOverviewConfig = useAppConfig();

  // The compiler infers `overviewMap` as the dependency while the source
  // narrows it to `overviewMap?.length`. That narrowing is deliberate and
  // correct: the body only ever reads `overviewMap?.length`, so widening the
  // dep to the whole array would change this callback's identity on every
  // overviewMap reference change and re-trigger the effects that depend on it
  // (re-running authentication / overview config fetches). Keep as-is.
  const fetchUserAndSetMetadata = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- narrowed dep (overviewMap?.length) is intentional; see above
    async (
      accessToken: string,
      isTokenRefresh: boolean = false,
      authenticateUser: boolean = true,
    ) => {
      // If this is a token refresh and another request is executing, just update the token
      if (isExecutingRef.current && isTokenRefresh) {
        setToken(ACCESS_TOKEN_COOKIE, accessToken);
        http.axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        return;
      }

      // Prevent concurrent executions
      if (isExecutingRef.current) {
        return;
      }

      // Prevent multiple authentications (unless it's a token refresh)
      if (!isTokenRefresh && hasExecutedRef.current && authenticateUser) {
        return;
      }

      isExecutingRef.current = true;

      try {
        dispatch(setHasColumnItemsFetchingError(false));
        dispatch(changeIsLoading(true));

        const { data } = await fetchUserData(accessToken);
        const parsedSettingsData = parseSettings(data.metadata);

        const organizationInputs = data.organizationSources.map((source) =>
          source.id.toLowerCase(),
        );
        const inputPermissions = data.userProfile.permissions.filter((input) =>
          organizationInputs.includes(input.toLowerCase()),
        );

        if (authenticateUser) {
          setToken(ACCESS_TOKEN_COOKIE, accessToken);
          http.axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        }

        const userLogin: User = {
          id: data.userProfile.id,
          firstName: data.userProfile.firstName,
          lastName: data.userProfile.lastName,
          email: data.userProfile.email,
          inputIds: inputPermissions,
          access: data.userProfile.roles,
          avatar: getAvatarUrl(data.userProfile.id),
          showWelcome: data.metadata.showWelcome,
          organization: data.organization,
          defaultAvatar: getDefaultAvatarForUser(data),
        };

        if (authenticateUser) {
          dispatch(login(userLogin));
        }

        const sorterKeys = [OVERVIEW_SORTER_KEY, ARCHIVE_SORTER_KEY];

        try {
          sorterKeys.forEach((sorterKey) => {
            const destructed =
              parsedSettingsData[sorterKey as keyof UserSettingsSorters];

            if (!destructed) {
              return;
            }

            const [key, sortDirection] = destructed.split(":");
            if (key && parsedSettingsData.saveSorter) {
              dispatch(
                setActiveSorter({
                  storageKey: sorterKey,
                  state: {
                    key: key as keyof DocumentSetColumns,
                    sortDirection: parseInt(sortDirection),
                  },
                }),
              );
            }
          });
        } catch {
          //pass
        }

        const overviewFilters = removeUnknownInputIdsFromFilters(
          inputPermissions,
          parsedSettingsData.overviewFilters,
        );
        const archiveFilters = removeUnknownInputIdsFromFilters(
          inputPermissions,
          parsedSettingsData.archiveFilters,
        );

        if (authenticateUser) {
          if (overviewFilters?.length || archiveFilters?.length) {
            dispatch(setShouldInitFilteringData());
          }

          await changeLanguage(parsedSettingsData.language);
        }

        // Apply the raw profile settings first. fetchAndSetOverviewConfig then
        // merges the server column items into overviewOrderColumns/
        // archiveOrderColumns and dispatches the result. If the order were
        // reversed, this dispatch would overwrite the merged column order with
        // the raw (empty for new users) values, causing the overview to flicker
        // between the merged order and the default fallback order.
        dispatch(updateUserSettings(parsedSettingsData));

        if (!isTokenRefresh || overviewMap?.length < 1) {
          await fetchAndSetOverviewConfig(
            userLogin,
            parsedSettingsData,
            authenticateUser,
          );
        }

        dispatch(setFilterArrayOverview(overviewFilters));
        dispatch(setFilterArrayArchive(archiveFilters));

        if (authenticateUser) {
          Sentry.setUser({
            id: data.userProfile.id,
            email: data.userProfile.email,
            ip_address: "{{auto}}",
          });
          hasExecutedRef.current = true;
        }
      } catch (e) {
        if (authenticateUser) {
          dispatch(setHasColumnItemsFetchingError(true));
        }
        console.error(e);
      } finally {
        dispatch(changeIsLoading(false));
        isExecutingRef.current = false;
      }
    },
    [
      dispatch,
      fetchAndSetOverviewConfig,
      overviewMap?.length,
      changeLanguage,
      setToken,
    ],
  );

  return { fetchUserAndSetMetadata };
};

export default useAuthentication;
